import CarImg from "assets/classA-car.png";
import { useEffect, useMemo, useRef, useState } from "react";
import { classActions } from "redux/slices/class";
import LocalStorage from "utils/localstorage.util";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useTranslation } from "react-i18next";
import DateService from "utils/date.util";
import useBookingTimeFormat from "hooks/useBookingTimeFormat";
import CurrencyDisplay from "components/atoms/CurrencyDisplay";
import JourneyMap from "../JourneyMap/JourneyMap";
import {
  getAggregatorDisplayCurrency,
  getHourlyDuration,
} from "utils/aggregatorHourlyPricing.util";
import ClassGallery from "./ClassGallery";
import type { GallerySlide } from "./ClassGallery";
import { isVanClass } from "./classGalleries";
import { toSlides } from "./classSlides";
import { LuggageArt, SeatingArt } from "./CapacityArt";
import {
  BagIcon,
  CarCancelIcon,
  CarIcon,
  ChargerIcon,
  ChauffeurIcon,
  ClockIcon,
  PaxIcon,
  ReceiptIcon,
  SeatIcon,
  SparkleIcon,
  TissueIcon,
  WaterIcon,
} from "./ClassIcons";

type MapPoint =
  | { lat: number; lng: number }
  | { lat: () => number; lng: () => number };

type PriceDetails = {
  brutto?: number;
  discount?: number;
  originalGross?: number;
  currency_short?: string;
  currency_sign?: string;
};

type ClassPrice = {
  class_id?: string | number;
  class_name?: string;
  vehicle_name?: string;
  class_image?: string;
  external_class_image?: string;
  max_persons?: string | number;
  max_bags?: string | number;
  max_pets?: string | number;
  max_kids?: string | number;
  notEnabled?: boolean;
  min_hours_hourlyTrip?: string | number;
  free_cancellation?: string | number;
  price_details?: PriceDetails;
  /* A Yelo class: the operator's free text from the classes collection (see
     PricesDTO in yelo-userback). A MyChauffeur-only class (Talixo or MC's own
     fleet, no matching Yelo class): MyChauffeur's `{ key, value }` list. */
  includes?: string | ProviderInclude[];
  /* yelo-userback master (mcClassDTO): MyChauffeur's list moved here and
     `includes` stays text — "" for a MyChauffeur-only class. */
  supplier_includes?: ProviderInclude[];
  complimentary?: string;
  waiting_time?: { airport?: number; others?: number };
  waiting_time_cost_per_min?: string | number;
  /* Backend-managed class media. */
  /* Operator-written per-class copy; falls back to generic wording. */
  headline?: string;
  description?: string;
  inclusions?: unknown;
  gallery?: unknown;
  luggage_gallery?: unknown;
  seating_gallery?: unknown;
  seating_gallery_version?: number;
};

type BookingPlace = {
  value?: string;
  details?: {
    name?: string;
    geometry?: {
      location?: MapPoint;
    };
  };
};

type SearchTripFormValues = {
  pickup?: BookingPlace;
  destination?: BookingPlace;
  duration?: string | number;
};

type BookingFormPayload = {
  trip_type?: string;
  pickup_type?: string;
  aggregator?: boolean;
  duration?: string | number;
  time_stamp?: string;
  distance?: string | number;
};

type InclusionItem = { key: string; icon: JSX.Element; label: string };

/* What a MyChauffeur class includes, as its API sends it — the contract in
   mc-companyback api/v2/Services/classIncludes.ts (MyChauffeur commit 11d503a,
   2026-10-20). `value` is minutes for free_waiting_time, hours for
   free_cancellation, otherwise `true`. MyChauffeur may add keys but never
   renames one; a key this list does not know yet is left out rather than
   shown raw. */
type ProviderInclude = { key: string; value: number | boolean };

const isProviderIncludeList = (value: unknown): value is ProviderInclude[] =>
  Array.isArray(value) &&
  value.every(
    (entry) =>
      !!entry &&
      typeof entry === "object" &&
      typeof (entry as ProviderInclude).key === "string"
  );

/* A positive number for `key` in the provider's list, else null. */
const providerAmount = (
  includes: ProviderInclude[] | null,
  key: string
): number | null => {
  const value = Number(includes?.find((entry) => entry.key === key)?.value);
  return Number.isFinite(value) && value > 0 ? value : null;
};

type InclusionInput =
  | string
  | { label?: string; text?: string; icon?: string };

/* The keys an operator can name in `inclusions[].icon`. Anything else, or
   nothing, falls back to the neutral mark. */
const INCLUSION_ICONS: Record<string, JSX.Element> = {
  chauffeur: <ChauffeurIcon />,
  wait: <ClockIcon />,
  clock: <ClockIcon />,
  cancellation: <CarCancelIcon />,
  charger: <ChargerIcon />,
  tissues: <TissueIcon />,
  water: <WaterIcon />,
  seat: <SeatIcon />,
  luggage: <BagIcon />,
  passengers: <PaxIcon />,
};

/* userback master sends `inclusions` as plain sentences, already translated
   (api/v2/utils/classInclusions.util.js), with no icon key — so every line
   fell back to the sparkle. Recognise the standard lines by their wording,
   English or Arabic, and give them the icons the built-in list uses. */
const INCLUSION_ICON_BY_TEXT: [RegExp, JSX.Element][] = [
  [/cancel|إلغاء/i, <CarCancelIcon />],
  [/wait|[اإ]نتظار/i, <ClockIcon />],
  [/water|مياه|ماء/i, <WaterIcon />],
  [/chauffeur|driver|سائق/i, <ChauffeurIcon />],
  [/vat|fees|ضريبة|رسوم/i, <ReceiptIcon />],
  [/meet|greet|استقبال/i, <PaxIcon />],
  [/flight|الرحلة الجوية/i, <ClockIcon />],
  [/door|الباب/i, <CarIcon />],
  [/charg|شاحن/i, <ChargerIcon />],
  [/tissue|مناديل/i, <TissueIcon />],
];

const iconForInclusionText = (label: string) =>
  INCLUSION_ICON_BY_TEXT.find(([pattern]) => pattern.test(label))?.[1];

type ServiceClassTabsProps = {
  onSubmit: () => void;
};

export default function ServiceClassTabs({ onSubmit }: ServiceClassTabsProps) {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation("translation");
  const { format: timeFormat } = useBookingTimeFormat();

  const [current, setCurrent] = useState<number>();
  const classTrack = useRef<HTMLDivElement>(null);
  const [railHasMore, setRailHasMore] = useState(false);
  const [railHasPrev, setRailHasPrev] = useState(false);
  const [capacityTab, setCapacityTab] = useState<"seating" | "luggage">(
    "seating"
  );
  const capacityPanelRef = useRef<HTMLDivElement>(null);
  /* Set only by a tab tap, so the first render and a class change never
     scroll the page on their own. */
  const capacityTabTapped = useRef(false);

  const selectCapacityTab = (tab: "seating" | "luggage") => {
    capacityTabTapped.current = true;
    setCapacityTab(tab);
  };

  /* Tapping Seating/Luggage near the foot of the screen swapped a gallery
     that sat mostly below the fold. Scroll just far enough that the whole
     gallery — image and dots — clears the fixed action bar at the bottom.
     A gallery already fully on screen does not move. */
  useEffect(() => {
    if (!capacityTabTapped.current) return;
    capacityTabTapped.current = false;
    const frame = window.requestAnimationFrame(() => {
      const gallery = capacityPanelRef.current?.querySelector(
        ".class-gallery-wrap"
      );
      if (!gallery) return;
      const gap = 16;
      /* The action bar is fixed over the foot of the screen, so the visible
         area ends at its top edge, not at the bottom of the window. */
      const bar = document.querySelector(".class-actionbar");
      const visibleBottom = bar
        ? bar.getBoundingClientRect().top
        : window.innerHeight;
      /* A sticky nav may sit over the top; the image must stay below it. */
      const nav = document.querySelector(".yp-shell-nav");
      const visibleTop = Math.max(0, nav?.getBoundingClientRect().bottom ?? 0);

      const rect = gallery.getBoundingClientRect();
      const overflow = rect.bottom - (visibleBottom - gap);
      /* Taller than the space available: align its top instead, so at least
         the picture is seen from the top down. */
      const room = rect.top - (visibleTop + gap);
      const delta = Math.min(overflow, room);
      if (delta > 0) window.scrollBy({ top: delta, behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [capacityTab]);
  const [mounted, setMounted] = useState(false);
  const [bookingFormPayload, setBookingFormPayload] =
    useState<BookingFormPayload | null>(null);
  const classsPrice = useAppSelector(
    (state) => state.classes.classsPrice
  ) as ClassPrice | undefined;
  const classesPrices = useAppSelector(
    (state) => state.classes.classesPrices
  ) as ClassPrice[] | undefined;

  const searchTripFormValues = useAppSelector(
    (state) => state.form?.PickUpForm?.values
  ) as SearchTripFormValues | undefined;

  const hourlyDuration = getHourlyDuration(
    bookingFormPayload,
    searchTripFormValues
  );

  const formDistance = useAppSelector(
    (state) => state.form?.SearchTripForm?.values?.distance
  );
  const distance = bookingFormPayload?.distance ?? formDistance;

  const isHourly = bookingFormPayload?.trip_type === "hourly";

  /* MyChauffeur-only classes describe their own inclusions; when they do,
     their waiting and cancellation figures are the ones to quote here too. */
  /* Backend master sends the list as `supplier_includes`; development still
     sends it in `includes`. Read whichever carries it. A Yelo class matched
     to a MyChauffeur one gets a supplier list too, but describes itself
     with the operator's own copy, so only a MyChauffeur-only class uses it. */
  const providerIncludes = (() => {
    if (!classsPrice) return null;
    if (isProviderIncludeList(classsPrice.includes))
      return classsPrice.includes.length ? classsPrice.includes : null;
    const isMcOnly = (classsPrice as { is_external_only?: boolean })
      .is_external_only;
    if (isMcOnly && isProviderIncludeList(classsPrice.supplier_includes))
      return classsPrice.supplier_includes.length
        ? classsPrice.supplier_includes
        : null;
    return null;
  })();

  const freeCancellationHours = (() => {
    /* Talixo classes arrive with free_cancellation 0; MyChauffeur's list
       carries the hours it actually honours. */
    const providerHours = providerAmount(providerIncludes, "free_cancellation");
    if (providerHours != null) return providerHours;
    if (bookingFormPayload?.aggregator) {
      const hours = Number(classsPrice?.free_cancellation);
      return Number.isFinite(hours) && hours > 0 ? hours : null;
    }
    return isHourly ? 12 : 4;
  })();

  const isAirportPickup = bookingFormPayload?.pickup_type === "airport";

  /* The classes collection stores real per-class wait minutes; prefer them over
     the flat 15/60 copy, and fall back to it when a class leaves them at 0. */
  const freeWaitMinutes = (() => {
    /* MyChauffeur already resolved airport vs elsewhere (and Talixo's
       `non_airport`, which waiting_time.others below would miss). */
    const providerMinutes = providerAmount(providerIncludes, "free_waiting_time");
    if (providerMinutes != null) return providerMinutes;
    const minutes = Number(
      isAirportPickup
        ? classsPrice?.waiting_time?.airport
        : classsPrice?.waiting_time?.others
    );
    return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
  })();

  /* An operator-supplied `inclusions` array wins outright, so a Yelo class and
     an external provider's class can describe themselves differently without
     this component changing. Without one, the standard Yelo chauffeur
     promises are shown, with the wait window and cancellation window taken
     from the class's own data wherever it carries them.

     (Worth revisiting once `inclusions[]` exists in PricesDTO: an aggregator's
     vehicle inherits these defaults today, and the chauffeur and water lines
     are the two nobody else has confirmed.) */
  /* One number, two places. "What's included" falls back to the flat 60/15
     copy when a class has no recorded waiting_time, and the fare conditions
     were only speaking when the data existed — so a class with no
     waiting_time promised 60 minutes in one section and said nothing about
     it in the other. Both now read this, so they cannot disagree.

     The 60/15 fallback is not new: the airportPickUp / freeWaitingTime
     strings already assert those minutes. It belongs in waiting_time on the
     class, and this resolves to that the moment it is populated. */
  const waitAllowanceMinutes =
    freeWaitMinutes ?? (isAirportPickup ? 60 : 15);

  const includedItems = useMemo(() => {
    if (!classsPrice) return [];

    const supplied = Array.isArray(classsPrice.inclusions)
      ? (classsPrice.inclusions as InclusionInput[])
      : null;

    if (supplied?.length) {
      return supplied
        .map((entry, index) => {
          const label =
            typeof entry === "string" ? entry : entry?.label ?? entry?.text;
          if (!label) return null;
          const iconKey = typeof entry === "string" ? undefined : entry?.icon;
          return {
            key: `supplied-${index}`,
            icon: INCLUSION_ICONS[iconKey ?? ""] ??
              iconForInclusionText(label) ?? <SparkleIcon />,
            label,
          };
        })
        .filter(Boolean) as InclusionItem[];
    }

    /* A MyChauffeur-only class: show exactly what MyChauffeur says it
       includes, in its order — not Yelo's own promises (a Talixo car, for
       one, has no complimentary water, and its driver is "certified", not
       one of ours). */
    if (providerIncludes?.length) {
      const items = providerIncludes
        .map((entry): InclusionItem | null => {
          switch (entry.key) {
            case "first_class_chauffeur":
              return {
                key: entry.key,
                icon: <ChauffeurIcon />,
                label: String(t("firstClassChauffeur")),
              };
            case "certified_driver":
              return {
                key: entry.key,
                icon: <ChauffeurIcon />,
                label: String(t("certifiedDriver")),
              };
            case "complimentary_water":
              return {
                key: entry.key,
                icon: <WaterIcon />,
                label: String(t("complimentaryWater")),
              };
            case "free_waiting_time":
              return {
                key: entry.key,
                icon: <ClockIcon />,
                label: isAirportPickup
                  ? String(t("airportWaitMinutes", { minutes: waitAllowanceMinutes }))
                  : String(t("freeWaitTimeMinutes", { minutes: waitAllowanceMinutes })),
              };
            case "free_cancellation":
              return freeCancellationHours != null
                ? {
                    key: entry.key,
                    icon: <CarCancelIcon />,
                    label: `${t("freeCancellation")} ${freeCancellationHours} ${t("until")}`,
                  }
                : null;
            case "meet_and_greet":
              return {
                key: entry.key,
                icon: <ChauffeurIcon />,
                label: String(t("meetAndGreetIncluded")),
              };
            case "flight_tracking":
              return {
                key: entry.key,
                icon: <ClockIcon />,
                label: String(t("flightTrackingIncluded")),
              };
            case "door_to_door":
              return {
                key: entry.key,
                icon: <SparkleIcon />,
                label: String(t("doorToDoorService")),
              };
            case "vat_and_fees":
              return {
                key: entry.key,
                icon: <SparkleIcon />,
                label: String(t("yp_includes_vat_fees")),
              };
            default:
              return null;
          }
        })
        .filter(Boolean) as InclusionItem[];

      if (classsPrice.complimentary) {
        items.push({
          key: "complimentary",
          icon: <SparkleIcon />,
          label: classsPrice.complimentary,
        });
      }
      return items;
    }

    const items: InclusionItem[] = [
      {
        key: "chauffeur",
        icon: <ChauffeurIcon />,
        label: String(t("professionalChauffeur")),
      },
      {
        key: "wait",
        icon: <ClockIcon />,
        label: isAirportPickup
          ? String(t("airportWaitMinutes", { minutes: waitAllowanceMinutes }))
          : String(t("freeWaitTimeMinutes", { minutes: waitAllowanceMinutes })),
      },
      {
        key: "water",
        icon: <WaterIcon />,
        label: String(t("complimentaryWater")),
      },
      /* Parking and tolls were only ever stated in the fare block, in a list
         of things the fare is not. They are a reason to book, so they read
         here with the rest of what you get. */
      {
        key: "noExtras",
        icon: <SparkleIcon />,
        label: String(t("noParkingOrTolls")),
      },
    ];

    if (freeCancellationHours != null && freeCancellationHours > 0) {
      items.push({
        key: "cancellation",
        icon: <CarCancelIcon />,
        label: `${t("freeCancellation")} ${freeCancellationHours} ${t("until")}`,
      });
    }

    /* Free text an operator typed against this class, when there is any. */
    if (typeof classsPrice.includes === "string" && classsPrice.includes.trim()) {
      items.push({
        key: "includes",
        icon: <SparkleIcon />,
        label: classsPrice.includes,
      });
    }
    if (classsPrice.complimentary) {
      items.push({
        key: "complimentary",
        icon: <SparkleIcon />,
        label: classsPrice.complimentary,
      });
    }

    return items;
  }, [
    classsPrice,
    providerIncludes,
    freeCancellationHours,
    waitAllowanceMinutes,
    isAirportPickup,
    t,
  ]);

  const getCurrency = (priceDetails?: PriceDetails) =>
    getAggregatorDisplayCurrency(priceDetails, bookingFormPayload?.aggregator);

  useEffect(() => {
    setMounted(true);
    setBookingFormPayload(
      LocalStorage.getItem("BookingFormPayload") as BookingFormPayload | null
    );
  }, []);

  /* Resolve the selection against the list that is actually on screen.

     Two failures this replaces. These were two effects, and a selection
     carried over from an earlier search fell between them: findIndex returned
     -1 because that class is not in the new results, so `current` was never
     updated, and the fallback never ran because `classsPrice` was still
     truthy — the rail kept the old index while the CTA named a class the list
     no longer contained.

     And a selection that *does* still exist in a new search was being
     restored, so a fresh set of prices opened on whichever class was picked
     last time rather than on the first. A new search is identified by the set
     of classes it returned; the same set means the user stepped back into
     this screen and their choice is theirs to keep. */
  const priceSetRef = useRef<string>("");

  useEffect(() => {
    if (!Array.isArray(classesPrices) || classesPrices.length === 0) return;

    const signature = classesPrices.map((el) => el.class_id).join("|");
    const isNewSearch = priceSetRef.current !== signature;
    priceSetRef.current = signature;

    const index =
      classsPrice && !isNewSearch
        ? classesPrices.findIndex((el) => el.class_id === classsPrice.class_id)
        : -1;

    if (index >= 0) {
      setCurrent(index);
      return;
    }

    setCurrent(0);
    if (classsPrice?.class_id !== classesPrices[0]?.class_id) {
      dispatch(classActions.setClasssPrice(classesPrices[0]));
    }
  }, [classsPrice, classesPrices, dispatch]);

  const handleClick = (price: ClassPrice, index: number) => {
    /* No notEnabled guard. A class below the journey's minimum is still
       worth opening — the hero and the notice above it are where you find
       out what the minimum is. Continue is what stays refused. */
    setCurrent(index);
    dispatch(classActions.setClasssPrice(price));
    classTrack.current?.children[index]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  /* Selected card, visible card and sticky CTA have to agree: coming back to
     this step showed the first class while the CTA still named the chosen one.
     Two things broke the old restore.
     
     It measured with getBoundingClientRect while the class images were still
     loading, so it centred against a layout that then moved under it. Offsets
     are read from the layout box instead, which does not depend on where the
     rail happens to be scrolled or painted at that instant.
     
     And `scroll-snap-type: x mandatory` re-runs snapping whenever the content
     resizes, which pulled the rail back to the first card as each image
     landed. Re-asserting the position once the rail's images have decoded
     settles it after the last of those reflows. */
  useEffect(() => {
    const track = classTrack.current;
    if (track == null || current == null || !mounted) return;

    const centreOnSelection = () => {
      const card = track.children[current] as HTMLElement | undefined;
      if (!card) return;
      // Horizontal only — restoring a selection must never jump the page.
      track.scrollLeft =
        card.offsetLeft -
        track.offsetLeft -
        (track.clientWidth - card.offsetWidth) / 2;
    };

    centreOnSelection();

    const images = Array.from(track.querySelectorAll("img"));
    const pending = images.filter((img) => !img.complete);
    if (!pending.length) return;

    let cancelled = false;
    const settle = () => {
      if (!cancelled) centreOnSelection();
    };
    pending.forEach((img) => {
      img.addEventListener("load", settle);
      img.addEventListener("error", settle);
    });
    return () => {
      cancelled = true;
      pending.forEach((img) => {
        img.removeEventListener("load", settle);
        img.removeEventListener("error", settle);
      });
    };
  }, [current, mounted]);

  /* Whether the strip still runs past the trailing edge. A peeking half-chip
     is the usual hint, but with chips sized to their own names the last one
     can land flush and the strip then looks complete when it is not. */
  useEffect(() => {
    const track = classTrack.current;
    if (!track) return;

    const read = () => {
      const max = track.scrollWidth - track.clientWidth;
      /* Math.abs for RTL, where Chrome reports scrollLeft negative. */
      const at = Math.abs(track.scrollLeft);
      setRailHasMore(max > 4 && at < max - 4);
      setRailHasPrev(at > 4);
    };

    read();
    track.addEventListener("scroll", read, { passive: true });
    window.addEventListener("resize", read);
    return () => {
      track.removeEventListener("scroll", read);
      window.removeEventListener("resize", read);
    };
  }, [classesPrices, mounted]);

  /* A little under one viewport per press, so a chip is always left on
     screen as an anchor rather than the strip jumping to new content. */
  const scrollRail = (direction: 1 | -1) => {
    const track = classTrack.current;
    if (!track) return;
    track.scrollBy({
      left: direction * Math.round(track.clientWidth * 0.8),
      behavior: "smooth",
    });
  };

  const renderAmount = (priceDetails?: PriceDetails, amount?: number) => {
    const currency = getCurrency(priceDetails);
    const value = amount?.toFixed?.(2);

    return (
      <>
        <em>
          <CurrencyDisplay currency={currency} />
        </em>
        {value}
      </>
    );
  };

  const selectedPrice = classsPrice?.price_details;
  const arrow = "→";

  const getClassImage = (classPrice: ClassPrice) =>
    classPrice.external_class_image || classPrice.class_image || CarImg;

  /* Each gallery ships with its slots captioned, so the slider — swipe, arrow
     and dots — is live before a single photo exists. An operator-supplied
     gallery on the class replaces these outright. */
  /* A count is only shown when the class actually carries one; a zero in the
     payload means "not recorded", not "none". */
  const countOf = (value?: string | number) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const seatCount = countOf(classsPrice?.max_persons);
  const bagCount = countOf(classsPrice?.max_bags);

  /* Seating and luggage each use a single carousel. Supplied photos lead;
     without them the class's own counts produce an illustrative diagram. */
  const capacitySlides = useMemo(() => {
    const isLuggage = capacityTab === "luggage";
    const diagram: GallerySlide = {
      id: `${capacityTab}-diagram`,
      /* Labelled as a drawing. It is built from this class's seat and bag
         counts, but it is not a photograph of this vehicle's cabin. */
      caption: `${
        isLuggage
          ? t("upToSuitcases", { count: bagCount ?? 0 })
          : t("upToPassengers", { count: seatCount ?? 0 })
      } · ${t("illustrativeLayout")}`,
      node: isLuggage ? (
        <LuggageArt bags={bagCount ?? 0} />
      ) : (
        <SeatingArt seats={seatCount ?? 0} />
      ),
    };

    /* Class photography is backend-managed. Keeping both galleries on the
       class record prevents bundled assets from drifting away from the
       vehicle and lets an operator replace media without a frontend build. */
    const supplied = isLuggage
      ? classsPrice?.luggage_gallery
      : classsPrice?.seating_gallery;

    const photos = Array.isArray(supplied)
      ? toSlides(
          supplied,
          [],
          capacityTab,
          i18n.resolvedLanguage || i18n.language
        )
      : [];

    /* The diagram is a stand-in, not a companion. Where a class has real
       photographs of its boot or cabin it leads with them, and the drawing
       drops out entirely — showing both made the first slide of a
       photographed class a cartoon of itself. Classes still waiting on
       photography keep the diagram, which is built from their own seat and
       bag counts. */
    if (photos.length) return photos;

    const hasCount = (isLuggage ? bagCount : seatCount) != null;
    return hasCount ? [diagram] : [];
  }, [
    capacityTab,
    classsPrice,
    bagCount,
    seatCount,
    t,
    i18n.resolvedLanguage,
    i18n.language,
  ]);

  return (
    <form
      className="shell layout class-select-layout"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <section className="class-drawer" aria-label={String(t("selectVehicle"))}>
        <div className="class-mobile-intro">
          <h2>{t("chooseYourExperience")}</h2>
          <p>{t("vehicleIntroKicker")}</p>
        </div>
        <div
          className={`classes-rail${railHasMore ? " has-more" : ""}${
            railHasPrev ? " has-prev" : ""
          }`}
        >
          {/* Real controls, not decoration. A hint that only says "there is
              more that way" leaves the reader to find the gesture; these
              move the strip. Each hides at its own end. */}
          <button
            type="button"
            className="classes-rail__nav classes-rail__prev"
            aria-label={String(t("previousClasses"))}
            onClick={() => scrollRail(-1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14.5 5.75 8.25 12l6.25 6.25" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                strokeLinejoin="round" />
            </svg>
          </button>
        <div
          className="classes"
          ref={classTrack}
          role="radiogroup"
          aria-label={String(t("selectVehicle"))}
        >
          {mounted &&
            Array.isArray(classesPrices) &&
            classesPrices.map((classPrice, index) => {
              const isSelected = current === index;
              const details = classPrice.price_details;

              return (
                <button
                  key={classPrice.class_id ?? index}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  /* A class below the journey's minimum duration is still
                     selectable: you have to be able to look at it to learn
                     why it will not work. Greying the chip and refusing the
                     tap hid both the class and the reason. The notice sits
                     above the hero instead, and Continue is what refuses. */
                  className={["class-card", isSelected ? "is-selected" : ""]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleClick(classPrice, index)}
                >
                  <span className="tick" aria-hidden="true">✓</span>

                  <span className="class-media">
                    <img
                      loading="lazy"
                      alt={classPrice.class_name}
                      src={getClassImage(classPrice)}
                    />
                  </span>

                  <span className="class-body">
                    <span className="class-name">{classPrice.class_name}</span>
                    <span className="class-model">
                      {classPrice.vehicle_name}
                    </span>

                    <span className="caps">
                      <span>
                        <i className="cap-icon" aria-hidden="true">
                          <PaxIcon />
                        </i>
                        {t("max")} <b>{classPrice.max_persons}</b>
                      </span>
                      <span>
                        <i className="cap-icon" aria-hidden="true">
                          <BagIcon />
                        </i>
                        <b>{classPrice.max_bags}</b> {t("bags")}
                      </span>
                    </span>
                  </span>

                  <span className="price">
                    <b>{renderAmount(details, details?.brutto)}</b>
                    {Number(details?.discount ?? 0) > 0 && (
                      <s className="price-was">
                        {renderAmount(details, details?.originalGross)}
                      </s>
                    )}
                    <small>
                      {bookingFormPayload?.aggregator
                        ? t("mc_price_vat_note")
                        : t("yp_includes_vat_fees")}
                    </small>
                  </span>
                </button>
              );
            })}
        </div>
          <button
            type="button"
            className="classes-rail__nav classes-rail__more"
            aria-label={String(t("moreClasses"))}
            onClick={() => scrollRail(1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9.5 5.75 6.25 6.25-6.25 6.25" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {mounted && classsPrice && (
          <>
            {classsPrice.notEnabled && (
              <p className="class-minimum" role="status">
                <span className="class-minimum__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <circle
                      cx="12"
                      cy="12"
                      r="8.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                    <path
                      d="M12 7.6v4.9l3 1.8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {t("this_class_are_allowed_minimum_duration_hours", {
                  hours: classsPrice.min_hours_hourlyTrip,
                })}
              </p>
            )}

            {/* One vehicle, large, with nothing competing. The class strip
                above carries the comparison; this carries the choice. */}
            <div className="class-hero">
              <img
                alt={String(classsPrice.class_name ?? "")}
                src={getClassImage(classsPrice)}
              />
              <div className="class-hero__identity">
                <strong>{classsPrice.class_name}</strong>
                {classsPrice.vehicle_name && (
                  <span className="class-hero__model">
                    {classsPrice.vehicle_name}
                  </span>
                )}
              </div>
            </div>

            <button
              type="button"
              className="class-detailcue"
              aria-controls="classDetail"
              onClick={() =>
                document.getElementById("classDetail")?.scrollIntoView({
                  behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
                    ? "auto"
                    : "smooth",
                  block: "start",
                })
              }
            >
              <span>{t("moreAboutThisClass")}</span>
              <span className="class-detailcue__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </button>

            <div className="class-lead">
              {/* Operator-written copy for this class when it exists. Nothing
                  generic stands in for it: a paragraph that reads the same
                  under First and under Business tells the reader nothing, and
                  inventing a distinction would be inventing a claim. */}
              {(classsPrice.headline || classsPrice.description) && (
                <>
                  {classsPrice.headline && (
                    <h3 className="class-lead__prop">{classsPrice.headline}</h3>
                  )}
                  {classsPrice.description && (
                    <p className="class-lead__sell">
                      {classsPrice.description}
                    </p>
                  )}
                </>
              )}

              {/* The chauffeur line is journey-specific: a name board in
                  arrivals and flight tracking are airport facts, and claiming
                  them for a city transfer would be describing a service the
                  passenger will not get. */}
              <div className="class-chauffeur">
                <span aria-hidden="true">
                  <ChauffeurIcon />
                </span>
                <p>
                  {isAirportPickup
                    ? t("chauffeurPromiseAirport")
                    : t("chauffeurPromiseStandard")}
                </p>
              </div>
            </div>

            <section
              className="class-detail"
              id="classDetail"
              aria-label={String(classsPrice.class_name ?? t("selectedExperience"))}
            >
              {/* What you get leads. It is the reason to choose this class at
                  all, and it sat below capacity where the reader had already
                  decided. Capacity follows, still open, because it is the
                  thing most likely to change which class you pick. */}
              {includedItems.length > 0 && (
                <details className="class-acc class-acc--included" open>
                  <summary>
                    <span>{t("whatsIncluded")}</span>
                    <em>{includedItems.length}</em>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor"
                        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </summary>
                  <div className="class-acc__body">
                    <ul
                      className={`class-included${
                        includedItems.length > 2 && includedItems.length % 2 === 1
                          ? " class-included--odd"
                          : ""
                      }`}
                    >
                      {includedItems.map((item) => (
                        <li key={item.key}>
                          <i aria-hidden="true">{item.icon}</i>
                          <span>{item.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              )}

              <details className="class-acc" open>
                <summary>
                  <span>{t("capacity")}</span>
                  <em>
                    {[
                      seatCount != null &&
                        `${t("seating")} ${seatCount}`,
                      bagCount != null && `${t("luggage")} ${bagCount}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </em>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor"
                      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </summary>
                <div className="class-acc__body">
                  <div
                    className="class-capacity__tabs"
                    role="tablist"
                  >
                    <button
                      type="button"
                      role="tab"
                      id="capacityTabSeating"
                      aria-selected={capacityTab === "seating"}
                      aria-controls="capacityPanel"
                      className={capacityTab === "seating" ? "is-active" : ""}
                      onClick={() => selectCapacityTab("seating")}
                    >
                      {t("seating")}
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="capacityTabLuggage"
                      aria-selected={capacityTab === "luggage"}
                      aria-controls="capacityPanel"
                      className={capacityTab === "luggage" ? "is-active" : ""}
                      onClick={() => selectCapacityTab("luggage")}
                    >
                      {t("luggage")}
                    </button>
                  </div>

                  <div
                    className={`class-capacity__panel class-capacity__panel--${capacityTab}`}
                    id="capacityPanel"
                    ref={capacityPanelRef}
                    role="tabpanel"
                    aria-labelledby={
                      capacityTab === "luggage"
                        ? "capacityTabLuggage"
                        : "capacityTabSeating"
                    }
                  >
                    <p className="class-capacity__note">
                      {capacityTab === "luggage"
                        ? isVanClass(classsPrice.class_name, classsPrice.vehicle_name)
                          ? t("vanLuggageCapacityNote", { count: bagCount ?? 0 })
                          : t("luggageCapacityNote")
                        : t("seatingCapacityNote")}
                    </p>

                    {/* One carousel: the diagram first, the photographs after
                        it. Dots and the arrow switch themselves on the moment
                        a second slide exists. */}
                    <ClassGallery
                      key={capacityTab}
                      slides={capacitySlides}
                      label={String(
                        capacityTab === "luggage" ? t("luggage") : t("seating")
                      )}
                      nextLabel={String(t("nextImage"))}
                      prevLabel={String(t("previousImage"))}
                      placeholderLabel={String(t("imageComingSoon"))}
                      placeholderIcon={
                        capacityTab === "luggage" ? <BagIcon /> : <SeatIcon />
                      }
                    />
                  </div>
                </div>
              </details>



              {/* {vehicleHasPhotos && (
                <details className="class-acc">
                  <summary>
                    <span>{t("exploreTheVehicle")}</span>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor"
                        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </summary>
                  <div className="class-acc__body">
                    <ClassGallery
                      slides={vehicleSlides}
                      label={String(classsPrice.class_name ?? t("selectVehicle"))}
                      nextLabel={String(t("nextImage"))}
                      prevLabel={String(t("previousImage"))}
                      placeholderLabel={String(t("imageComingSoon"))}
                      placeholderIcon={<CarIcon />}
                    />
                  </div>
                </details>
              )} */}
            </section>
          </>
        )}
      </section>

      <aside className="rail" aria-label={String(t("yp_your_journey"))}>
        <div className="panel journey-panel">
          <figure className="journey-map">
            <JourneyMap
              pickup={searchTripFormValues?.pickup?.details?.geometry?.location}
              pickupKicker={String(t("pickup"))}
              pickupLabel={searchTripFormValues?.pickup?.details?.name}
              pickupMeta={DateService.getDisplayTimeString(
                bookingFormPayload?.time_stamp,
                timeFormat
              )}
              destination={
                isHourly
                  ? null
                  : searchTripFormValues?.destination?.details?.geometry
                      ?.location
              }
              destinationKicker={String(t("destination"))}
              destinationLabel={
                isHourly
                  ? undefined
                  : searchTripFormValues?.destination?.details?.name
              }
              destinationMeta={distance ? `${distance} ${t("km")}` : undefined}
            />
          </figure>

          <div className="journey-content">
            <p className="when">
              {DateService.getShortDateString(bookingFormPayload?.time_stamp)}
              {" · "}
              {DateService.getDisplayTimeString(
                bookingFormPayload?.time_stamp,
                timeFormat
              )}
            </p>

            <div className="leg">
              <span className="pin" aria-hidden="true">
                <b />
                <s />
              </span>
              <div>
                <span className="route-label-row">
                  <small className="route-label">{t("pickup")}</small>
                  {!isHourly && distance ? (
                    <span className="route-metrics">
                      {distance} {t("km")}
                    </span>
                  ) : null}
                </span>
                <strong>{searchTripFormValues?.pickup?.details?.name}</strong>
                <em>{searchTripFormValues?.pickup?.value}</em>
              </div>

              <span className="pin end" aria-hidden="true">
                <b />
              </span>
              <div>
                <small className="route-label">
                  {isHourly ? t("duration") : t("destination")}
                </small>
                <strong>
                  {isHourly
                    ? `${hourlyDuration} ${t("hours")}`
                    : searchTripFormValues?.destination?.details?.name}
                </strong>
                <em>
                  {isHourly
                    ? `${t("incl")} ${(hourlyDuration ?? 0) * 40} ${t("km")}`
                    : searchTripFormValues?.destination?.value}
                </em>
              </div>
            </div>

            {mounted && selectedPrice && (
              <dl className="total">
                <dt>{t("total")}</dt>
                <dd>{renderAmount(selectedPrice, selectedPrice.brutto)}</dd>
              </dl>
            )}

            <button
              type="submit"
              className="btn block"
              disabled={!mounted || !selectedPrice || classsPrice?.notEnabled}
            >
              {t("choose vehicle")} <span aria-hidden="true">{arrow}</span>
            </button>
          </div>
        </div>

        <section
          className="benefit-section"
          aria-labelledby="benefitSectionTitle"
        >
          <div className="benefit-section-head">
            <strong id="benefitSectionTitle">
              {t("All classes include")}
            </strong>
          </div>
          {/* Same source as the phone's "What's included": whatever the class
              actually carries. This used to assert a chauffeur, free waiting
              time and bottled water for every class on the list, including an
              external provider's, which the payload does not confirm.
              The VAT chip stays — it describes the fare we quote, not a
              service someone else has to deliver. */}
          <div className="benefit-chips">
            {includedItems.map((item) => (
              <div className="benefit-chip" key={item.key}>
                <i aria-hidden="true">✓</i>
                <span>
                  <b>{item.label}</b>
                </span>
              </div>
            ))}
            <div className="benefit-chip">
              <i aria-hidden="true">✓</i>
              <span>
                <b>{t("yp_vat_and_fees")}</b>
                <small>{t("yp_included_in_fare")}</small>
              </span>
            </div>
          </div>
        </section>
      </aside>

      <div className="class-actionbar">
        {/* What the number covers, and what the button does. "Select Business"
            read as though it completed the booking; it opens the next step. */}
        <div className="class-actionbar-total">
          <span className="class-actionbar-label">{t("totalForJourney")}</span>
          <span className="class-actionbar-amount" aria-live="polite">
            {mounted && selectedPrice
              ? renderAmount(selectedPrice, selectedPrice.brutto)
              : null}
          </span>
        </div>

        <button
          type="submit"
          className="btn block book-now"
          disabled={!mounted || !selectedPrice || classsPrice?.notEnabled}
        >
          <span className="book-now__label">
            {classsPrice
              ? t("continueWithClass", { className: classsPrice.class_name })
              : t("continue")}
          </span>
        </button>
      </div>

    </form>
  );
}
